import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import io from "socket.io-client";
import DeckGL from "@deck.gl/react";
import Map from "react-map-gl/maplibre";
import { ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import "maplibre-gl/dist/maplibre-gl.css";

const SOCKET_URL = process.env.REACT_APP_API_URL || window.location.origin;
const MAP_STYLE  = "https://api.maptiler.com/maps/base-v4/style.json?key=qjoXX9L2A2sutJRBuk7t";

const TEAMS = {
  port_royale: { label: "Port Royale", color: "#92400e", bg: "#fffbeb", border: "#f59e0b", light: "#fef3c7" },
  tortuga:     { label: "Tortuga",     color: "#1e3a8a", bg: "#eff6ff", border: "#60a5fa", light: "#dbeafe" },
};

const OPTION_LABELS = ["A", "B", "C", "D"];

function AdminMap({ viewport, marker, markerLabel, revealed }) {
  const [view, setView] = useState({
    ...viewport, pitch: 0, bearing: 0,
    minZoom: viewport.zoom - 1, maxZoom: viewport.zoom + 4,
  });

  useEffect(() => {
    setView({
      ...viewport, pitch: 0, bearing: 0,
      minZoom: viewport.zoom - 1, maxZoom: viewport.zoom + 4,
      transitionDuration: 600,
    });
  }, [viewport.longitude, viewport.latitude, viewport.zoom]);

  const layers = useMemo(() => {
    if (!marker) return [];

    const dotLayer = new ScatterplotLayer({
      id: "admin-marker",
      data: [marker],
      getPosition: d => [d.longitude, d.latitude],
      getFillColor: [220, 38, 38, 230],
      getRadius: 50000,
      radiusUnits: "meters",
      radiusMinPixels: 10,
      radiusMaxPixels: 30,
      stroked: true,
      getLineColor: [255, 255, 255, 255],
      lineWidthMinPixels: 2,
    });

    if (!revealed || !markerLabel) return [dotLayer];

    const labelLayer = new TextLayer({
      id: "admin-marker-label",
      data: [{ position: [marker.longitude, marker.latitude + 0.8], text: markerLabel }],
      getPosition: d => d.position,
      getText: d => d.text,
      getSize: 14,
      getColor: [30, 30, 30, 255],
      getBackgroundColor: [255, 255, 255, 220],
      background: true,
      backgroundPadding: [6, 3, 6, 3],
      getBorderColor: [200, 200, 200, 255],
      getBorderWidth: 1,
      fontWeight: "bold",
      getAnchor: "middle",
      getAlignmentBaseline: "bottom",
    });

    return [dotLayer, labelLayer];
  }, [marker, markerLabel, revealed]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      <DeckGL
        viewState={view}
        onViewStateChange={({ viewState }) => setView(viewState)}
        controller={true}
        layers={layers}
        style={{ position: "absolute", inset: 0 }}
        useDevicePixels={true}
      >
        <Map
          mapStyle={MAP_STYLE}
          onLoad={(e) => {
            const map = e.target;
            map.getStyle().layers.forEach((layer) => {
              if (layer.type === "symbol")
                map.setLayoutProperty(layer.id, "visibility", "none");
            });
          }}
        />
      </DeckGL>
    </div>
  );
}

function ScoreBar({ teams }) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      {Object.entries(TEAMS).map(([tid, cfg]) => {
        const t = teams[tid] || {};
        return (
          <div key={tid} style={{
            flex: 1, background: cfg.bg, border: `1.5px solid ${cfg.border}`,
            borderRadius: 10, padding: "10px 14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14, color: cfg.color }}>{cfg.label}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                {t.player_count ?? 0} player{t.player_count !== 1 ? "s" : ""}
              </div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 500, color: cfg.color }}>{t.score ?? 0}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminQuiz() {
  const socketRef = useRef(null);
  const [connected, setConnected]   = useState(false);
  const [gameState, setGameState]   = useState(null);
  const [revealData, setRevealData] = useState(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.on("connect",    () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("state", (s) => {
      setGameState(s);
      if (s.phase === "question") setRevealData(null);
    });
    socket.on("reveal_data", (d) => setRevealData(d));
    return () => socket.disconnect();
  }, []);

  const emit = useCallback((ev, data) => socketRef.current?.emit(ev, data), []);

  if (!connected || !gameState) {
    return (
      <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#9ca3af", fontSize: 14 }}>
        {connected ? "Loading…" : "Connecting…"}
      </div>
    );
  }

  const q            = gameState.current_question;
  const isRevealed   = gameState.phase === "reveal";
  const isMapQ       = q?.type === "map";
  const totalPlayers = Object.values(gameState.teams).reduce((s, t) => s + t.player_count, 0);

  const getVotes = (tid, i) => revealData?.votes?.[tid]?.[i] ?? 0;
  const getTotal = (tid)    => revealData?.totals?.[tid]      ?? 0;

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh" }}>

      {/* ── LOBBY ── */}
      {gameState.phase === "lobby" && (
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "2.5rem 1rem" }}>
          <h1 style={{ fontSize: 24, fontWeight: 500, marginBottom: 4 }}>Transatlantic Trade Quiz</h1>
          <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 28 }}>
            Admin panel · {totalPlayers} player{totalPlayers !== 1 ? "s" : ""} connected
            <span style={{
              marginLeft: 10, display: "inline-block",
              width: 7, height: 7, borderRadius: "50%",
              background: connected ? "#4ade80" : "#f87171",
              verticalAlign: "middle",
            }} />
          </p>

          <ScoreBar teams={gameState.teams} />

          <div style={{ marginTop: 32, textAlign: "center", width: "100%", display: "flex", justifyContent: "space-between"}}>
            <button onClick={() => emit("start_game")} style={{
              padding: "14px 48px", borderRadius: 10, border: "none",
              background: "#111827", color: "#fff", fontWeight: 500, fontSize: 16, cursor: "pointer",
            }}>
              Start game
            </button>
            <button onClick={() => emit("reset_game")} style={{
                padding: "14px 20px", borderRadius: 10,
                border: "1px solid #e5e7eb", background: "#fff",
                color: "#374151", fontSize: 14, cursor: "pointer",
            }}>
                Reset
            </button>
          </div>
        </div>
      )}

      {/* ── QUESTION / REVEAL ── */}
      {(gameState.phase === "question" || gameState.phase === "reveal") && q && (
        <>
          {/* Large map on top, full width */}
          {isMapQ && (
            <div style={{ height: "60vh", overflow: "hidden" }}>
              <AdminMap
                viewport={q.viewport}
                marker={q.marker}
                markerLabel={revealData?.marker_label}
                revealed={isRevealed}
              />
            </div>
          )}

          <div style={{ maxWidth: 680, margin: "0 auto", padding: "1.5rem 1rem" }}>

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
              <span>Question {gameState.question_index + 1} / {gameState.total_questions}</span>
              <span style={{
                background: isRevealed ? "#f0fdf4" : "#fffbeb",
                color: isRevealed ? "#166534" : "#92400e",
                padding: "2px 8px", borderRadius: 4, fontSize: 11,
              }}>
                {isRevealed ? "Revealed" : "Answering…"}
              </span>
            </div>

            <div style={{ marginBottom: 16 }}><ScoreBar teams={gameState.teams} /></div>

            <div style={{
              background: "#1e293b", color: "#f1f5f9", borderRadius: 12,
              padding: "14px 18px", fontSize: 16, fontWeight: 500,
              lineHeight: 1.5, marginBottom: 14,
            }}>
              {q.text}
            </div>

            {/* Options with live vote counts */}
            <div>
              {q.options.map((opt, i) => {
                const isCorrect  = isRevealed && i === revealData?.correct_index;
                const totalVotes = Object.keys(TEAMS).reduce((s, tid) => s + getVotes(tid, i), 0);
                const totalAll   = Object.keys(TEAMS).reduce((s, tid) => s + getTotal(tid), 0);
                const pct        = totalAll > 0 ? Math.round((totalVotes / totalAll) * 100) : 0;

                return (
                  <div key={i}>
                    <button style={{
                      display: "flex", alignItems: "center", width: "100%",
                      textAlign: "left", padding: "10px 14px",
                      borderRadius: isRevealed ? "10px 10px 0 0" : 10,
                      border: `1.5px solid ${isCorrect ? "#4ade80" : "#e5e7eb"}`,
                      background: isCorrect ? "#f0fdf4" : "#fff",
                      color: isCorrect ? "#166534" : "#111827",
                      cursor: "default", fontSize: 14,
                      marginBottom: isRevealed ? 0 : 8,
                    }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                        background: isCorrect ? "#4ade80" : "#f3f4f6",
                        color: isCorrect ? "#166534" : "#374151",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 500, marginRight: 10,
                      }}>
                        {OPTION_LABELS[i]}
                      </span>
                      <span style={{ flex: 1 }}>{opt}</span>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        {Object.entries(TEAMS).map(([tid, tcfg]) => (
                          <span key={tid} style={{ fontSize: 11, color: tcfg.color }}>
                            {tcfg.label}: {getVotes(tid, i)}
                          </span>
                        ))}
                        {isRevealed && totalAll > 0 && (
                          <span style={{ fontSize: 11, color: "#9ca3af" }}>({pct}%)</span>
                        )}
                        {isRevealed && isCorrect && (
                          <span style={{ fontSize: 11, color: "#166534" }}>✓ correct</span>
                        )}
                      </div>
                    </button>

                    {/* Vote bars */}
                    {isRevealed && (
                      <div style={{
                        padding: "6px 14px 10px",
                        background: isCorrect ? "#f0fdf4" : "#fff",
                        border: `1.5px solid ${isCorrect ? "#4ade80" : "#e5e7eb"}`,
                        borderTop: "none",
                        borderRadius: "0 0 10px 10px",
                        marginBottom: 8, display: "flex", gap: 8,
                      }}>
                        {Object.entries(TEAMS).map(([tid, tcfg]) => {
                          const v   = getVotes(tid, i);
                          const tot = getTotal(tid);
                          const p   = tot > 0 ? Math.round((v / tot) * 100) : 0;
                          return (
                            <div key={tid} style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: tcfg.color, marginBottom: 3 }}>
                                {tcfg.label}: {v}/{tot}
                              </div>
                              <div style={{ background: "#e5e7eb", borderRadius: 4, height: 5 }}>
                                <div style={{
                                  width: `${p}%`, height: 5, background: tcfg.border,
                                  borderRadius: 4, transition: "width 0.5s",
                                }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isRevealed && revealData?.marker_label && (
              <div style={{
                background: "#f8fafc", border: "1px solid #e2e8f0",
                borderRadius: 8, padding: "8px 14px", marginBottom: 14,
                fontSize: 13, color: "#475569", display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#dc2626", display: "inline-block", flexShrink: 0 }} />
                <strong>{revealData.marker_label}</strong>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              {!isRevealed && (
                <button onClick={() => emit("reveal")} style={{
                  flex: 1, padding: "13px", borderRadius: 10, border: "none",
                  background: "#f59e0b", color: "#fff", fontWeight: 500, fontSize: 15, cursor: "pointer",
                }}>
                  Reveal answers
                </button>
              )}
              {isRevealed && (
                <button onClick={() => emit("next_question")} style={{
                  flex: 1, padding: "13px", borderRadius: 10, border: "none",
                  background: "#111827", color: "#fff", fontWeight: 500, fontSize: 15, cursor: "pointer",
                }}>
                  {gameState.question_index + 1 >= gameState.total_questions
                    ? "See final scores →"
                    : "Next question →"}
                </button>
              )}
              <button onClick={() => emit("reset_game")} style={{
                    padding: "13px 20px", borderRadius: 10,
                    border: "1px solid #e5e7eb", background: "#fff",
                    color: "#374151", fontSize: 14, cursor: "pointer",
                }}>
                    Restart
                </button>
            </div>

            <p style={{ textAlign: "center", fontSize: 11, color: "#d1d5db", marginTop: 8 }}>
              {totalPlayers} player{totalPlayers !== 1 ? "s" : ""} connected
              <span style={{
                marginLeft: 8, display: "inline-block",
                width: 6, height: 6, borderRadius: "50%",
                background: connected ? "#4ade80" : "#f87171",
                verticalAlign: "middle",
              }} />
            </p>
          </div>
        </>
      )}

      {/* ── FINISHED ── */}
      {gameState.phase === "finished" && (
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "4rem 1rem", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🏆</div>
          <ScoreBar teams={gameState.teams} />
          <button onClick={() => emit("reset_game")} style={{
            marginTop: 32, padding: "12px 36px", borderRadius: 10, border: "none",
            background: "#111827", color: "#fff", fontWeight: 500, fontSize: 15, cursor: "pointer",
          }}>
            Reset &amp; play again
          </button>
        </div>
      )}
    </div>
  );
}